import { Store } from "../../utils/Store";
import { SessionStore } from "../../uidispatcherapi/SessionStore";
import { CartContextActions } from "../../actions/CartContextActions";
import { CustomerCartActions } from "../../actions/CustomerCartActions";
import { OrderEntryActions } from "../../actions/OrderEntryActions";
import deepEquals from "fast-deep-equal/es6";
export type CartContext = {
	country: string | null | undefined;
	locale: string | null | undefined;
	customerId: string | null | undefined;
	cardholderNumber: string | null | undefined;
	storeId: string | null | undefined;
	fsdAddressId: string | null | undefined;
};
export type CartContextMode = "SESSION" | "URL";
type UrlModeTriggers = {
	onEnterUrlMode: any;
	onExitUrlMode: any;
};
export class CartContextStoreImpl extends Store {
	_state!: CartContext & {
		mode: CartContextMode;
		cartId: string | null | undefined;
	};

	constructor({ onEnterUrlMode, onExitUrlMode }: UrlModeTriggers) {
		super();
		this.listenTo(onEnterUrlMode, (...args) => {
			// @ts-expect-error
			this.handleEnterUrlMode(...args);
		});
		this.listenTo(onExitUrlMode, (...args) => {
			// @ts-expect-error
			this.handleExitUrlMode(...args);
		});
		this.listenTo(CartContextActions.onSelectCustomer, (...args) => {
			// @ts-expect-error
			this.handleSelectCustomer(...args);
		});
		this.listenTo(CartContextActions.onSelectCardholder, (...args) => {
			// @ts-expect-error
			this.handleOnSelectCardholder(...args);
		});
		this.listenTo(CustomerCartActions.onRemoveCartFromContext, (cartId) => {
			this.handleResetCart(cartId);
		});
		this.listenTo(SessionStore, this.handleSessionChange);
		this.resetState();
	}

	resetState(cartId?: string | null) {
		this._state = { ...this.getContextFromSession(), cartId: cartId || null, mode: "SESSION" };
		this.trigger();
	}

	handleResetCart(cartId: string | null | undefined) {
		if (cartId && this._state.cartId === cartId) {
			this._state = { ...this._state, cartId: null };
			this.trigger();
		}
	}

	handleOnSelectCardholder(cardholderNumber: string) {
		if (this._state.mode !== "SESSION") {
			return;
		}

		if (this._state.cardholderNumber !== cardholderNumber) {
			this._state = { ...this._state, cardholderNumber, cartId: null };
			this.trigger();
		}
	}

	handleSessionChange() {
		const { mode, cartId, ...prevContext } = this._state;

		if (mode !== "SESSION") {
			return;
		}

		const nextContext = this.getContextFromSession();

		if (!deepEquals(nextContext, prevContext)) {
			this._state = {
				mode,
				cartId: null,
				...nextContext,
			};
			this.trigger();
		}
	}

	handleEnterUrlMode(
		cartId: string,
		customerId: string,
		cardholderNumber: string,
		fsdAddressId: string,
		storeId: string,
	) {
		const nextContext: CartContext = {
			customerId,
			cardholderNumber,
			storeId,
			fsdAddressId,
			country: SessionStore.getCountry(),
			locale: SessionStore.getLocale(),
		};

		this._state = { ...nextContext, cartId, mode: "URL" };
		// Allow navigating to the customer-facing from FOE without loosing the customer selection.
		SessionStore.setSelectedCustomerId(customerId, cardholderNumber);
		SessionStore.setDeliveryAddress({
			hash: fsdAddressId,
			integrationId: fsdAddressId,
			deliveryStore: storeId,
		});
		OrderEntryActions.onSelectCardholder(cardholderNumber);
		this.trigger();
	}

	handleExitUrlMode() {
		const { cartId } = this._state;
		this.resetState(cartId);
	}

	handleSelectCustomer(customerId: string | null | undefined, cardholderNumber: string | null | undefined) {
		this._state = {
			customerId,
			cardholderNumber,
			country: SessionStore.getCountry(),
			locale: SessionStore.getLocale(),
			cartId: null,
			storeId: null,
			fsdAddressId: null,
			mode: "SESSION",
		};

		if (customerId && cardholderNumber) {
			SessionStore.setSelectedCustomerId(customerId, cardholderNumber);
		}

		this.trigger();
	}

	getContextFromSession(): CartContext {
		return {
			country: SessionStore.getCountry(),
			locale: SessionStore.getLocale(),
			customerId: SessionStore.getCustomerId(),
			cardholderNumber: SessionStore.isEmployee()
				? this._state?.cardholderNumber
				: SessionStore.getCardholderNumber(),
			storeId: SessionStore.getSelectedStoreId(),
			fsdAddressId: SessionStore.getSelectedDeliveryAddressId() || null,
		};
	}

	getState() {
		return { ...this._state };
	}
}
export const CustomerCartContextStore = new CartContextStoreImpl({
	onEnterUrlMode: CartContextActions.onOpenOrderEntry,
	onExitUrlMode: CartContextActions.onCloseOrderEntry,
});
export const SavedCartContextStore = new CartContextStoreImpl({
	onEnterUrlMode: CartContextActions.onOpenSavedCart,
	onExitUrlMode: CartContextActions.onCloseSavedCart,
});
